import type { AnimationI, AnySceneObjectT, EaseT, SimpleStyleT, TimelineI } from "tweenly"
import gsap from "gsap"
import { imageSrc } from "../data/styles/simple/display.styles"
import { S3_URL } from "./file.helpers"
import { buildSequenceDescriptors, EMPTY_IMAGE_SRC, roundNumber, SequenceDescriptor } from "tweenly"

// ------------------------------------
// CLEAR ALL OLD REFERENCES OF GSAP
// ------------------------------------
export const resetGsapTimelines = () => {
    if (!gsap) return

    try {
        // Kill every child of the root timeline.
        //
        // This used to be `gsap.killTweensOf("*")`, which is NOT a "kill everything" idiom.
        // GSAP's `toArray()` treats a string as a SELECTOR (gsap-core.js:646), so "*" becomes
        // `document.querySelectorAll("*")` — every element in the document, sliced into a
        // fresh array. `getTweensOf()` then walks the root timeline and, for each child, runs
        // `_arrayContainsAny(child._targets, parsedTargets)` (gsap-core.js:153), a linear scan
        // of that list of tens of thousands of nodes.
        //
        // It ran at the start of every `updateMasterTimeline`, i.e. at the end of every drag,
        // and the profile showed it as the widest, deepest block of a 3.6 s frozen frame.
        // Walking the children we actually created is both cheaper and more thorough — the
        // selector version never touched tweens whose target is not a DOM element, such as
        // the `gsap.to({}, { duration: 0 })` placeholders.
        gsap.globalTimeline.getChildren(true, true, true).forEach((child) => child.kill())
        // kill global timeline
        gsap.globalTimeline.kill()
        gsap.globalTimeline.clear()
    } catch (err) {
        console.warn?.("GSAP cleanup failed", err)
    }
}

// ------------------------------------
// SERIALIZE ANIMATION
// ------------------------------------

const serializeAnimationStateForDownload = (animationStates: SimpleStyleT[]): string => {
    const numOfStates = animationStates.length
    let result: string = ""

    animationStates.forEach((animationState: SimpleStyleT, index: number) => {
        // Object.assign(result, { [animationState.property as string]: animationState.value })
        const keys = animationState.property.split(".")
        keys.forEach((key, index) => {
            if (index > 0) result += "{"
            result += key + ":"
            if (index === keys.length - 1)
                result += `'${animationState.value}${
                    animationState.unit === "%" ? animationState.unit : ""
                }'${"}".repeat(index)}`
        })

        if (index < numOfStates - 1) result += ","
    })
    return result
}

const serializeAnimationEaseForDownload = (ease: EaseT): string => {
    let result: string = ""
    if (ease) {
        result = ease.easeStyle + "." + ease.easeType
        const strength = ease.easeStrengthValues.find((s) => s.strength === ease.easeStrength)
        if (strength) result += strength.value
    }
    return result
}

// ------------------------------------
// INIT ANIMATION
// ------------------------------------

const initTweenForDownload = (animation: AnimationI, isPreview: boolean): string => {
    let gsapTween: string = ""

    if (animation?.tween.type === "fromTo")
        gsapTween = `masterTimeline.fromTo("#${
            animation.objectId
        }", {${serializeAnimationStateForDownload(
            animation.tween.from
        )}}, {${serializeAnimationStateForDownload(animation.tween.to)}, duration:${
            animation.duration
        }, ease:"${serializeAnimationEaseForDownload(animation.ease)}"}, ${animation.delay});
            `
    else if (animation.tween.type === "from")
        gsapTween = `masterTimeline.from("#${
            animation.objectId
        }", {${serializeAnimationStateForDownload(animation.tween.from)}, duration:${
            animation.duration
        }, ease:"${serializeAnimationEaseForDownload(animation.ease)}"}, ${animation.delay});
            `
    else if (animation.tween.type === "to")
        gsapTween = `masterTimeline.to("#${
            animation.objectId
        }", {${serializeAnimationStateForDownload(animation.tween.to)}, duration:${
            animation.duration
        }, ease:"${serializeAnimationEaseForDownload(animation.ease)}"}, ${animation.delay});
            `
    else if (animation.tween.type === "set") {
        gsapTween = `masterTimeline.set("#${
            animation.objectId
        }", {${serializeAnimationStateForDownload(animation.tween.from)}}, ${animation.delay});
            `
    }
    return gsapTween
}

// ------------------------------------
// VALIDATE JAVASCRIPT CODE
// ------------------------------------

interface SkippedCodeTrigger {
    type: "timeline" | "label" | "animation"
    trigger: "onInit" | "onUpdate" | "onLabel"
    name: string
    reason: "comments-only" | "syntax-error"
}

const isValidAndExecutableJavaScript = (
    code: string,
    skippedTriggers?: SkippedCodeTrigger[],
    triggerInfo?: Omit<SkippedCodeTrigger, "reason">
): boolean => {
    const pushSkipped = (reason: SkippedCodeTrigger["reason"]) => {
        if (!skippedTriggers || !triggerInfo) return
        const duplicateExists = skippedTriggers.some(
            (entry) =>
                entry.type === triggerInfo.type &&
                entry.trigger === triggerInfo.trigger &&
                entry.name === triggerInfo.name &&
                entry.reason === reason
        )
        if (duplicateExists) return
        skippedTriggers.push({ ...triggerInfo, reason })
    }

    if (!code || code.trim() === "") {
        return true
    }

    let codeWithoutComments = code.replace(/\/\/.*/g, "")
    codeWithoutComments = codeWithoutComments.replace(/\/\*[\s\S]*?\*\//g, "")
    if (codeWithoutComments.trim() === "") {
        pushSkipped("comments-only")
        return false
    }

    try {
        // Use Function constructor to validate syntax without executing
        // eslint-disable-next-line no-new-func
        new Function(code)
        return true
    } catch {
        pushSkipped("syntax-error")
        return false
    }
}

const normalizeCodeForExport = (code: string): string =>
    code.replaceAll("&apos;", "'").replaceAll("&apos", "'").replaceAll('\\"', '"')

const escapeClosingScriptTag = (code: string): string => code.replace(/<\/script/gi, "<\\/script")

/** Escape special characters for use inside JS single-quoted string literals */
const escapeForJsString = (value: string): string =>
    value
        .replaceAll("\\", "\\\\")
        .replaceAll("'", "\\'")
        .replaceAll("\n", "\\n")
        .replaceAll("\r", "\\r")

const sanitizeIdentifierSegment = (value: string): string => value.replace(/[^a-zA-Z0-9_$]/g, "_")

const generateSkippedWarningsForExport = (skippedTriggers: SkippedCodeTrigger[]): string => {
    if (!skippedTriggers.length) return ""

    return skippedTriggers
        .map((t) => {
            const msg = `[WARNING]: not valid ${t.trigger} code in ${t.type}:${t.name} - reason: ${t.reason}`
            return `console.warn(${JSON.stringify(msg)});`
        })
        .join("\n")
}

// ------------------------------------
// IMAGE SEQUENCE PLAYER (EXPORT)
// ------------------------------------

/**
 * The decode-gated sequence-player runtime lives in the `tweenly` package
 * (`src/sequencePlayer` in tweenly-lib), loaded by the exported HTML as
 * `scripts/tweenly.umd.cjs`. The generator only hands it the descriptor table and
 * paints the initial frame; live painting is wired into the master timeline's
 * `onUpdate` (see `onUpdateTrigger`). This is the exact same runtime the editor
 * canvas preview consumes, so export and preview behave identically.
 */
const buildSequencePlayerConfig = (descriptors: SequenceDescriptor[]): string => `
        window.tweenly = window.tweenly || {}
        window.tweenly.imageSequenceStorageURLBase =
            window.tweenly.imageSequenceStorageURLBase || ""
        // Origin the exported frames are authored against (absolute S3 urls). When an
        // operator sets imageSequenceStorageURLBase (#1), the runtime swaps this origin
        // for that base so frames are served from the proxy instead of S3 (#3).
        window.tweenly.imageSequenceSourceURLBase =
            window.tweenly.imageSequenceSourceURLBase || ${JSON.stringify(S3_URL)}
        window.tweenly.configureSequencePlayer(${JSON.stringify(descriptors)})
        window.tweenly.paintSequences()
`

// ------------------------------------
// INIT MASTER TIMELINE
// ------------------------------------

export const initMasterTimelineForDownload = (
    timeline: TimelineI,
    animations: AnimationI[],
    objects: AnySceneObjectT[],
    freeVersionHtml: boolean
    // TO DO use timeline from editor.settings
): string => {
    // Track skipped code triggers for user notification
    const skippedTriggers: SkippedCodeTrigger[] = []

    const initializedMasterTimeline: string = `
        masterTimeline = new gsap.timeline();
    `
    let masterTimelineOnInit: string = ""
    let masterTimelineOnEnd: string = ""
    if (objects.find((object) => object.type === "ticker")) {
        objects.forEach((object) => {
            const parentObject = object.parentId
                ? objects.find((obj) => obj.id === object.parentId)
                : null

            if (object.type === "ticker" || (object.parentId && parentObject?.type === "ticker")) {
                masterTimelineOnInit =
                    masterTimelineOnInit +
                    `
                    masterTimeline.set("#${object.id}", { visibility: "${
                        object.isHidden ? "hidden" : "visible"
                    }" });
                    `
                masterTimelineOnEnd =
                    masterTimelineOnEnd +
                    `
                    masterTimeline.set("#${object.id}", { visibility: "${
                        object.isHidden ? "hidden" : "visible"
                    }" });
                    `
            } else {
                masterTimelineOnInit =
                    masterTimelineOnInit +
                    `
                    masterTimeline.set("#${object.id}", { visibility: "${
                        object.isHidden ? "hidden" : "visible"
                    }" });
                    `
                masterTimelineOnEnd =
                    masterTimelineOnEnd +
                    `
                    masterTimeline.set("#${object.id}", { visibility: "${
                        object.isHidden ? "hidden" : "visible"
                    }" });
                    `
            }
        })
    } else {
        masterTimelineOnInit = `
            masterTimeline.set("#canvas", { visibility: "visible" });
        `
        masterTimelineOnEnd = `
            masterTimeline.set("#canvas", { visibility: "visible" });
        `
    }

    // init animations
    const { descriptors: sequenceDescriptors, frameIndexById } =
        buildSequenceDescriptors(animations)
    const hasSequences = sequenceDescriptors.length > 0
    const initializedTweens = getInitializedTweens(animations, false, frameIndexById)
    const createLabels: string =
        `
        window.timelineLabels = [` +
        timeline.labels
            ?.map((label) => {
                return (
                    "{" +
                    `title: '${escapeForJsString(label.title)}',` +
                    `time: ${label.time},` +
                    `type: '${label.type}',` +
                    `jumpto: '${escapeForJsString(label.jumpto ?? "")}'` +
                    "}"
                )
            })
            .join(",") +
        `];
    `

    const timelineOnUpdateCode: string =
        !freeVersionHtml &&
        timeline.onUpdateCode &&
        isValidAndExecutableJavaScript(
            normalizeCodeForExport(timeline.onUpdateCode.code),
            skippedTriggers,
            {
                type: "timeline",
                trigger: "onUpdate",
                name: "TimelineOnUpdate",
            }
        )
            ? `
    const timelineOnUpdate = (time, lasttime, duration) => {
        ${normalizeCodeForExport(timeline.onUpdateCode.code)}

    }`
            : `
    const timelineOnUpdate = (time, lasttime, duration) => {}`

    const labelsCode = (): string => {
        var result: string = ""

        timeline.labels.forEach((label) => {
            const safeLabelTitle = sanitizeIdentifierSegment(label.title)
            result +=
                !freeVersionHtml &&
                label.onLabelCode &&
                isValidAndExecutableJavaScript(
                    normalizeCodeForExport(label.onLabelCode.code),
                    skippedTriggers,
                    {
                        type: "label",
                        trigger: "onLabel",
                        name: label.title,
                    }
                )
                    ? `
        const label_${safeLabelTitle}_onLabel = () => {
            ${normalizeCodeForExport(label.onLabelCode.code)}
        }
        `
                    : `
        const label_${safeLabelTitle}_onLabel = () => {
        }
        `
        })

        result += `const labelsOnLabel = (labeltitle) => {
        `

        timeline.labels.forEach((label) => {
            const safeLabelTitle = sanitizeIdentifierSegment(label.title)
            result += `if (labeltitle === '${escapeForJsString(
                label.title
            )}') { label_${safeLabelTitle}_onLabel() }
        `
        })

        result += `}
        `

        return result
    }

    type PreparedAnimationCode = {
        animation: AnimationI
        functionNameBase: string
        onInitCode: string | null
        onUpdateCode: string | null
    }

    const getValidatedCodeForExport = (
        code: string | undefined,
        triggerInfo: Omit<SkippedCodeTrigger, "reason">
    ): string | null => {
        if (!code) return null
        const normalized = normalizeCodeForExport(code)
        if (!isValidAndExecutableJavaScript(normalized, skippedTriggers, triggerInfo)) return null
        return escapeClosingScriptTag(normalized)
    }

    const prepareAnimationCode = (animation: AnimationI): PreparedAnimationCode => {
        const safeTitle = sanitizeIdentifierSegment(animation.title)
        const safeId = sanitizeIdentifierSegment(animation.id)
        const functionNameBase = `animation_${safeTitle}_${safeId}`

        const onInitCode = getValidatedCodeForExport(animation.onInitCode?.code, {
            type: "animation",
            trigger: "onInit",
            name: animation.id,
        })
        const onUpdateCode = getValidatedCodeForExport(animation.onUpdateCode?.code, {
            type: "animation",
            trigger: "onUpdate",
            name: animation.id,
        })

        return {
            animation,
            functionNameBase,
            onInitCode,
            onUpdateCode,
        }
    }

    const initAnimationCode = (prepared: PreparedAnimationCode) => {
        let animationCode: string = ""
        const { functionNameBase, onInitCode, onUpdateCode } = prepared

        if (onInitCode) {
            animationCode += `const ${functionNameBase}_onInit = () => {
                ${onInitCode}
            }
            `
        }
        if (onUpdateCode) {
            animationCode += `const ${functionNameBase}_onUpdate = (time, lasttime, duration) => {
                ${onUpdateCode}
            }
            `
        }
        return animationCode
    }

    const triggerAnimationCode = (prepared: PreparedAnimationCode) => {
        let animationCode: string = ""
        const { animation, functionNameBase, onInitCode, onUpdateCode } = prepared

        if (onInitCode) {
            animationCode += `if ((lasttime === 0 && ${animation.delay} <= time) || (lasttime > 0 && ${animation.delay} > lasttime && ${animation.delay} <= time)) ${functionNameBase}_onInit()
                `
        }
        if (onUpdateCode) {
            animationCode += `if (${animation.delay} <= time && time <= ${
                animation.delay + animation.duration
            }) ${functionNameBase}_onUpdate(time - ${animation.delay}, lasttime - ${
                animation.delay
            }, ${animation.duration})
                `
        }
        return animationCode
    }

    const animationsCode = (): string => {
        var result: string = ""
        const preparedAnimations = animations.map((animation) => prepareAnimationCode(animation))

        result = preparedAnimations.map((prepared) => initAnimationCode(prepared)).join("")

        result += `
            const triggerAnimationsCode = (time, lasttime) => {
                `
        result += preparedAnimations.map((prepared) => triggerAnimationCode(prepared)).join("")

        result += `
            }
        `

        return result
    }

    const onUpdateTrigger: string = `
        window.timelineOnUpdate = timelineOnUpdate
        window.labelsOnLabel = labelsOnLabel
        window.triggerAnimationsCode = triggerAnimationsCode

        const updateTimeline = () => {
            window.tweenly.updateTimeline()
            ${hasSequences ? "window.tweenly.paintSequences()" : ""}
        }

        masterTimeline.eventCallback('onUpdate', updateTimeline)
    `
    // prettier-ignore
    const timelineOnInitCode: string = !freeVersionHtml &&
    timeline.onInitCode &&
    isValidAndExecutableJavaScript(normalizeCodeForExport(timeline.onInitCode.code), skippedTriggers, {
        type: "timeline",
        trigger: "onInit",
        name: "TimelineOnInit",
    }) ?
    `const timelineOnInit = () => {
${normalizeCodeForExport(timeline.onInitCode.code)}
}` : `const timelineOnInit = () => {}`
    const labelsCodeForExport = labelsCode()
    const animationsCodeForExport = animationsCode()

    // Hand the shared tweenly runtime the descriptor table + paint the initial
    // frame; live painting is driven by onUpdate. Runs inside initTimeline so
    // tweenly.umd.cjs and the `data-tw-seq` markers (initializedTweens) are ready.
    const sequencePlayerConfig = hasSequences ? buildSequencePlayerConfig(sequenceDescriptors) : ""

    const warningsForExport = generateSkippedWarningsForExport(skippedTriggers)

    return `
    window.masterTimeline = null;
    ${timelineOnInitCode}
    const initTimeline = () => {
            ${warningsForExport}
            ${initializedMasterTimeline}
            ${masterTimelineOnInit}
            ${initializedTweens}
            ${masterTimelineOnEnd}
            ${createLabels}
            ${timelineOnUpdateCode}
            ${labelsCodeForExport}
            ${animationsCodeForExport}
            ${sequencePlayerConfig}
            ${onUpdateTrigger}
            try {timelineOnInit()} catch(error) {}
        };
    `
}

const getInitializedTweens = (
    animations: AnimationI[],
    isPreview: boolean,
    frameIndexById?: Record<string, number>
) => {
    // When frameIndexById is provided (export/download path), sequence frames emit a
    // declarative `data-tw-seq` index marker consumed by the decode-gated player
    // instead of a raw `attr.src` write. Preview keeps the legacy src behavior.
    const useSequenceMarkers = !!frameIndexById
    const initializedTweens: string[] = []
    animations.forEach((animation) => {
        if (animation.subTimeline) return

        if (useSequenceMarkers && animation.tween.name === "sequence") {
            const index = frameIndexById![animation.id] ?? 0
            initializedTweens.push(
                `masterTimeline.set("#${animation.objectId}", {attr:{"data-tw-seq": ${index}}}, ${animation.delay});
                `
            )
        } else {
            const tween = initTweenForDownload(animation!, isPreview)
            initializedTweens.push(tween)
        }

        if (animation.tween.name === "sequence") {
            const nextDelay = roundNumber(animation.delay + animation.duration, 2)
            const nextAnimation = Object.values(animations).find(
                (a) =>
                    a &&
                    a.objectId === animation.objectId &&
                    roundNumber(a.delay, 2) === nextDelay &&
                    a.tween.name === "sequence"
            )
            if (!nextAnimation) {
                const emptyTween = useSequenceMarkers
                    ? `masterTimeline.set("#${animation.objectId}", {attr:{"data-tw-seq": -1}}, ${nextDelay});
                `
                    : `masterTimeline.set("#${
                          animation.objectId
                      }", {${serializeAnimationStateForDownload([
                          imageSrc(EMPTY_IMAGE_SRC),
                      ])}}, ${nextDelay});
                `
                initializedTweens.push(emptyTween)
            }
        }
    })
    return initializedTweens.join(" ")
}
